import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {
    ChangeClientIdFilterPayload,
    ChangeNameFilterPayload,
    ChangeStateFilterPayload,
    PushToSurveyListPayload,
    RemoveSurveyListItemPayload,
    ReplaceSurveyListItemPayload,
    SavePaginationKeyPayload,
    StateFilterValue,
    SurveysStore,
    UpdateSurveyListItemPayload,
    isSurveyItem,
    FetchSurveyListPayload,
    ChangeOrgIdFilterPayload
} from 'client/common/store/reducers/surveys/types';

import {FILTER_KEY, sessionStorageClient} from 'client/common/utils/storage';

export const initialState: SurveysStore = {
    filter: sessionStorageClient.getDataFromStorage(FILTER_KEY) ?? {
        state: StateFilterValue.All
    },
    isEntirelyLoaded: false,
    IsLoadingSurveysList: false,
    list: []
};

const slice = createSlice({
    name: 'surveys',
    initialState,
    reducers: {
        clearSurveyList(state) {
            state.isEntirelyLoaded = initialState.isEntirelyLoaded;
            state.list = initialState.list;
            state.paginationKey = initialState.paginationKey;
        },
        fetchSurveyList(state, _action: PayloadAction<FetchSurveyListPayload>) {
            state.IsLoadingSurveysList = true;

            return;
        },
        savePaginationKey(state, action: PayloadAction<SavePaginationKeyPayload>) {
            const {paginationKey} = action.payload;

            state.paginationKey = paginationKey;
        },
        resetFilter(state) {
            state.filter = initialState.filter;
        },
        changeClientIdFilter(state, action: PayloadAction<ChangeClientIdFilterPayload>) {
            const {clientId} = action.payload;

            state.filter.clientId = clientId;
        },
        changeStateFilter(state, action: PayloadAction<ChangeStateFilterPayload>) {
            const {state: surveyState} = action.payload;

            state.filter.state = surveyState;
        },
        changeNameFilter(state, action: PayloadAction<ChangeNameFilterPayload>) {
            const {name} = action.payload;

            state.filter.name = name;
        },
        changeOrgFilter(state, action: PayloadAction<ChangeOrgIdFilterPayload>) {
            const {orgIds} = action.payload;

            state.filter.orgIds = orgIds && orgIds > 0 ? orgIds : undefined;
        },
        pushToSurveyList(state, action: PayloadAction<PushToSurveyListPayload>) {
            const {position, items} = action.payload;

            state.list.splice(position ?? state.list.length, 0, ...items);
            state.IsLoadingSurveysList = false;
        },
        replaceSurveyListItem(state, action: PayloadAction<ReplaceSurveyListItemPayload>) {
            const {position, item} = action.payload;

            state.list[position] = item;
        },
        updateSurveyListItem(state, action: PayloadAction<UpdateSurveyListItemPayload>) {
            const {slug, fields} = action.payload;

            state.list = state.list.map(item => {
                if (isSurveyItem(item) && item.slug === slug) {
                    return {...item, ...fields};
                }

                return item;
            });
        },
        removeSurveyListItem(state, action: PayloadAction<RemoveSurveyListItemPayload>) {
            const {slug} = action.payload;

            state.list = state.list.filter(item => !isSurveyItem(item) || item.slug !== slug);
        },
        setSurveyListIsEntirelyLoaded(state) {
            state.isEntirelyLoaded = true;
        }
    }
});

const {actions, reducer} = slice;

export const {
    changeClientIdFilter,
    changeNameFilter,
    changeOrgFilter,
    changeStateFilter,
    clearSurveyList,
    fetchSurveyList,
    pushToSurveyList,
    removeSurveyListItem,
    replaceSurveyListItem,
    resetFilter,
    savePaginationKey,
    setSurveyListIsEntirelyLoaded,
    updateSurveyListItem
} = actions;

export default reducer;
